import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useNotificationContext } from "contexts/NotificationContext";
import { useNavigate } from "react-router-dom";
import { RouteName } from "constants/route";
import { ICreatePlace, IPlace } from "models/place";
import createPlaceValidator from "validators/createPlace.validator";
import { createPlaceApi, placeSearchApi } from "api/place";
import { ILocation } from "models/dispatch";
import {
  MapCameraChangedEvent,
  MapMouseEvent,
} from "@vis.gl/react-google-maps";

export default function useCreatePlace() {
  const navigate = useNavigate();
  const { showError, showSuccess } = useNotificationContext();
  const [loading, setLoading] = useState<boolean>(false);
  const [places, setPlaces] = useState<IPlace[]>([]);
  const [zoom, setZoom] = useState<number>(14);
  const [viewPlace, setViewPlace] = useState<IPlace | null>(null);
  const [selectedLocation, setSelectedLocation] = useState<{
    lat: number;
    lng: number;
  }>();
  const [currentLocation, setCurrentLocation] = useState<ILocation>({
    lat: 13.496494229233356,
    lng: 39.47494624044569,
  });
  const { control, handleSubmit, watch, setValue } = useForm<ICreatePlace>({
    resolver: yupResolver(createPlaceValidator),
  });
  const watchLatitude = watch("latitude");
  const watchLongitude = watch("longitude");

  useEffect(() => {
    fetchPlaces();
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition((value) => {
        if (value?.coords?.latitude && value?.coords?.longitude)
          setCurrentLocation({
            lat: value.coords.latitude,
            lng: value.coords.longitude,
          });
      });
    }
  }, []); // eslint-disable-line

  const fetchPlaces = async () => {
    const { data, error } = await placeSearchApi("");
    if (data) setPlaces(data.data);
    else showError(error!.message);
  };

  useEffect(() => {
    if (watchLatitude && watchLongitude)
      setSelectedLocation({
        lat: Number(watchLatitude),
        lng: Number(watchLongitude),
      });
  }, [watchLatitude, watchLongitude]);

  const zoomLevel = useMemo(() => {
    if (zoom === 22) return 0.000013;
    if (zoom === 21) return 0.000026;
    if (zoom === 20) return 0.000052;
    if (zoom === 19) return 0.000104;
    if (zoom === 18) return 0.000208;
    if (zoom === 17) return 0.000416;
    if (zoom === 16) return 0.000832;
    if (zoom === 15) return 0.001664;
    if (zoom === 14) return 0.003328;
    if (zoom === 13) return 0.006656;
    if (zoom === 12) return 0.013312;
    if (zoom === 11) return 0.026624;
    if (zoom === 10) return 0.053248;
    if (zoom === 9) return 0.106496;
    if (zoom === 8) return 0.212992;
    if (zoom === 7) return 0.425984;
    if (zoom === 6) return 0.851968;
    if (zoom === 5) return 1.703936;
    if (zoom === 4) return 3.407872;
    if (zoom === 3) return 6.815744;
    if (zoom === 2) return 13.631488;
    if (zoom === 1) return 27.262976;
    if (zoom === 0) return 54.525952;
    // Default zoom level for any other case

    return 0.000013;
  }, [zoom]);

  const onSubmit = async (input: ICreatePlace) => {
    setLoading(true);
    const { data, error } = await createPlaceApi(input);
    if (data) {
      showSuccess("Place created Successfully");
      navigate(RouteName.PLACE, { replace: true });
    } else {
      showError(error!.message);
    }
    setLoading(false);
  };

  const handleLocationSelected = (event: MapMouseEvent) => {
    if (event.detail.latLng?.lat && event.detail.latLng?.lng) {
      setValue("latitude", event.detail.latLng.lat.toString());
      setValue("longitude", event.detail.latLng.lng.toString());
    }
  };

  const handleShowPlace = (place: IPlace) => setViewPlace(place);
  const handleClosePlace = () => setViewPlace(null);
  const handleZoomChanged = (e: MapCameraChangedEvent) => {
    console.log("Zoom changed", e.detail.zoom);
    setZoom(e.detail.zoom);
  };

  return {
    handleSubmit,
    onSubmit,
    handleLocationSelected,
    handleShowPlace,
    handleClosePlace,
    handleZoomChanged,
    zoomLevel,
    viewPlace,
    selectedLocation,
    control,
    loading,
    currentLocation,
    places,
  };
}
